<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import { addToast } from '../../lib/toast'
  import { api } from '../../lib/api'

  export let sessionId: string
  export let sessionType: string = 'telegram_bot' // Por defecto telegram_bot

  const dispatch = createEventDispatcher()

  let to = ''
  let messageText = ''
  let filePath = ''
  let fileCaption = ''
  let activeTab: 'text' | 'file' = 'text'
  let sending = false

  // Validación específica de Telegram
  function validateTelegramChatID(chatId: string): { valid: boolean; error?: string } {
    if (!chatId || chatId.trim() === '') {
      return { valid: false, error: 'El destinatario es requerido' }
    }

    // Remover espacios
    const cleaned = chatId.trim()

    // Para MTProto, acepta números de teléfono o @username
    if (sessionType === 'telegram_mtproto') {
      // Validar que sea un número de teléfono (con +) o un @username
      if (cleaned.startsWith('@')) {
        // Validar formato de username básico
        if (!/^@[a-zA-Z0-9_]{5,32}$/.test(cleaned)) {
          return { valid: false, error: 'Username inválido. Debe tener entre 5 y 32 caracteres alfanuméricos o guiones bajos (ej: @username)' }
        }
        return { valid: true }
      }
      // Validar número de teléfono (debe empezar con +)
      if (cleaned.startsWith('+')) {
        // Validar que después del + haya solo dígitos
        if (!/^\+\d{7,15}$/.test(cleaned)) {
          return { valid: false, error: 'Número de teléfono inválido. Debe tener formato internacional (ej: +51972053723)' }
        }
        return { valid: true }
      }
      // También acepta números sin + (para compatibilidad)
      if (/^\d{7,15}$/.test(cleaned)) {
        return { valid: true }
      }
      return { valid: false, error: 'Destinatario inválido. Debe ser un número de teléfono (ej: +51972053723) o un @username (ej: @username)' }
    }

    // Para Telegram Bot, solo acepta Chat ID numérico o @username
    if (cleaned.startsWith('@')) {
      // Validar formato de username básico
      if (!/^@[a-zA-Z0-9_]{5,32}$/.test(cleaned)) {
        return { valid: false, error: 'Username inválido. Debe tener entre 5 y 32 caracteres alfanuméricos o guiones bajos (ej: @username)' }
      }
      return { valid: true }
    }

    // Validar que sea un número válido (Chat ID)
    if (!/^-?\d+$/.test(cleaned)) {
      return { valid: false, error: 'Chat ID inválido. Debe ser un número (ej: 123456789) o un @username (ej: @username). Para obtener el Chat ID, envía un mensaje al bot y consulta: https://api.telegram.org/bot<TOKEN>/getUpdates' }
    }

    return { valid: true }
  }

  function validateMessage(message: string): { valid: boolean; error?: string } {
    if (!message || message.trim() === '') {
      return { valid: false, error: 'El mensaje es requerido' }
    }

    if (message.length > 4096) {
      return { valid: false, error: 'El mensaje no puede exceder 4096 caracteres' }
    }

    return { valid: true }
  }

  function validateFilePath(path: string): { valid: boolean; error?: string } {
    if (!path || path.trim() === '') {
      return { valid: false, error: 'La ruta o URL del archivo es requerida' }
    }

    // Validar que sea una URL válida o una ruta local
    const isURL = /^https?:\/\/.+/.test(path.trim())
    const isLocalPath = /^\/.+/.test(path.trim()) || /^[A-Za-z]:/.test(path.trim())

    if (!isURL && !isLocalPath) {
      return { valid: false, error: 'Debe ser una URL (https://...) o una ruta local (/ruta/archivo.jpg)' }
    }

    return { valid: true }
  }

  async function sendMessage() {
    // Validar destinatario
    const toValidation = validateTelegramChatID(to)
    if (!toValidation.valid) {
      addToast(toValidation.error || 'Chat ID inválido', 'error')
      return
    }

    if (activeTab === 'text') {
      // Validar mensaje
      const msgValidation = validateMessage(messageText)
      if (!msgValidation.valid) {
        addToast(msgValidation.error || 'Mensaje inválido', 'error')
        return
      }

      sending = true
      try {
        await api.post('/api/v1/sendText', {
          sessionId: sessionId,
          to: to.trim(),
          text: messageText
        })
        addToast('Mensaje enviado correctamente', 'success')
        messageText = ''
        to = ''
        dispatch('sent')
      } catch (e: any) {
        addToast(e.message || 'Error al enviar mensaje', 'error')
      } finally {
        sending = false
      }
    } else {
      // Validar archivo
      const fileValidation = validateFilePath(filePath)
      if (!fileValidation.valid) {
        addToast(fileValidation.error || 'Ruta de archivo inválida', 'error')
        return
      }

      sending = true
      try {
        await api.post('/api/v1/sendImage', {
          sessionId: sessionId,
          to: to.trim(),
          path: filePath.trim(),
          caption: fileCaption
        })
        addToast('Archivo enviado correctamente', 'success')
        filePath = ''
        fileCaption = ''
        to = ''
        dispatch('sent')
      } catch (e: any) {
        addToast(e.message || 'Error al enviar archivo', 'error')
      } finally {
        sending = false
      }
    }
  }
</script>

<div class="space-y-5">
  <!-- Identificador del formulario -->
  <div class="flex items-center gap-3 pb-4 border-b border-white/10">
    <div class="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/30">
      <span class="text-2xl">✈️</span>
    </div>
    <div>
      <h3 class="text-lg font-black text-white">Formulario de Telegram</h3>
      <p class="text-xs text-indigo-200/60">Envía mensajes y archivos a Chat IDs o @usernames</p>
    </div>
  </div>

  <!-- Tabs -->
  <div class="flex gap-2 border-b border-white/10">
    <button
      on:click={() => activeTab = 'text'}
      class="px-6 py-3 font-bold text-sm transition-all relative {activeTab === 'text' ? 'text-pink-400' : 'text-indigo-200/60 hover:text-indigo-200'}"
    >
      💬 Mensaje de Texto
      {#if activeTab === 'text'}
        <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500"></div>
      {/if}
    </button>
    <button
      on:click={() => activeTab = 'file'}
      class="px-6 py-3 font-bold text-sm transition-all relative {activeTab === 'file' ? 'text-pink-400' : 'text-indigo-200/60 hover:text-indigo-200'}"
    >
      🖼️ Archivo
      {#if activeTab === 'file'}
        <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500"></div>
      {/if}
    </button>
  </div>

  <!-- Destinatario -->
  <div>
    <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
      Destinatario {sessionType === 'telegram_mtproto' ? '(Teléfono o @username)' : '(Chat ID)'}
    </label>
    <input 
      bind:value={to}
      placeholder={sessionType === 'telegram_mtproto' ? 'Ej: +51972053723 o @username' : 'Ej: 123456789 o @username'}
      class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
    />
    {#if sessionType === 'telegram_mtproto'}
      <p class="text-sm text-indigo-300/80 mt-2 ml-1 font-medium">
        Ingresa un número de teléfono en formato internacional (ej: +51972053723) o un @username (ej: @username). Puedes enviar mensajes a cualquier contacto de Telegram.
      </p>
    {:else}
      <p class="text-sm text-indigo-300/80 mt-2 ml-1 font-medium">
        Ingresa el Chat ID numérico (ej: 123456789) o un @username (ej: @username). Para obtener el Chat ID, envía un mensaje al bot y consulta: 
        <code class="text-xs bg-black/30 px-1 rounded">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>
      </p>
    {/if}
  </div>

  <!-- Contenido según tab -->
  {#if activeTab === 'text'}
    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Mensaje</label>
      <textarea 
        bind:value={messageText}
        rows="4"
        placeholder="Escribe tu mensaje aquí..."
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 resize-none"
      ></textarea>
      <p class="text-xs text-indigo-300/60 mt-1 ml-1">Máximo 4096 caracteres</p>
    </div>
  {:else}
    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Ruta o URL del archivo</label>
      <div class="relative group">
        <input 
          bind:value={filePath}
          placeholder="https://ejemplo.com/imagen.jpg o /ruta/local/imagen.jpg"
          class="w-full px-4 py-3 pl-10 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
        />
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-indigo-200/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
      </div>
      <p class="text-xs text-indigo-300/60 mt-1 ml-1">Telegram soporta URLs y rutas locales para imágenes</p>
    </div>

    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Caption (Opcional)</label>
      <input 
        bind:value={fileCaption}
        placeholder="Descripción del archivo..."
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
      />
    </div>
  {/if}

  <!-- Botón Enviar -->
  <button
    on:click={sendMessage}
    disabled={sending || !to}
    class="w-full px-6 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
  >
    {sending ? 'Enviando...' : '📤 Enviar Mensaje'}
  </button>
</div>

